import React from 'react';
import cx from 'classnames';
import { DecorationProps } from './InputField.types';
import styles from './InputField.module.scss';
import Tooltip from '../ToolTip';
import ErrorText from './ErrorText';
import ParsedText from '../../atoms/ParsedText';
import { Sizing } from '../../atoms/Text/Text.types';

const RadioButtonsDecoration = ({
  className = '',
  labelClasses = '',
  ariaHidden = false,
  title,
  description,
  errorMessage,
  children,
  isFullWidth,
  color = 'primary',
  hideError = false,
  reducedMargin = false,
  noMargin = false,
  invalid,
  errorMsgHolderId,
  removeDescriptionMargin = false,
}: DecorationProps) => {
  return (
    <fieldset
      className={cx(className, styles.fieldset, styles.inputGroup, {
        [styles.fullWidth]: isFullWidth,
        [styles.reducedMargin]: reducedMargin,
        [styles.noMargin]: noMargin,
      })}
      aria-hidden={ariaHidden}
      aria-invalid={invalid}>
      {title && <legend className={cx(styles[color], styles.legend, labelClasses)}>{title}</legend>}
      {description && (
        <div
          className={cx(styles.description, {
            [styles.removeDescriptionMargin]: removeDescriptionMargin,
          })}>
          <ParsedText htmlString={description} />
        </div>
      )}
      {children}
      {!hideError && <ErrorText id={errorMsgHolderId}>{errorMessage}</ErrorText>}
    </fieldset>
  );
};

const CheckboxDecoration = ({
  labelClasses = '',
  className = '',
  ariaHidden = false,
  title,
  description,
  errorMessage,
  inputId,
  children,
  isFullWidth,
  errorMsgHolderId,
  color = 'primary',
  hideError = false,
  reducedMargin = false,
  noMargin = false,
  removeDescriptionMargin = false,
  hideEmptyError = false,
  sizing = Sizing.Regular,
}: DecorationProps) => {
  return (
    <div
      className={cx(className, styles.inputGroup, {
        [styles.fullWidth]: isFullWidth,
        [styles.reducedMargin]: reducedMargin,
        [styles.noMargin]: noMargin,
      })}>
      <label className={labelClasses} htmlFor={inputId} aria-hidden={ariaHidden}>
        {children}
        {title && (
          <ParsedText
            sizing={sizing}
            htmlString={title}
            textProps={{ className: cx(styles[color], styles.checkboxText) }}
          />
        )}
        {description && (
          <div
            className={cx(styles.description, {
              [styles.removeDescriptionMargin]: removeDescriptionMargin,
            })}>
            <ParsedText htmlString={description} />
          </div>
        )}
      </label>
      {!hideError && (errorMessage || !hideEmptyError) && <ErrorText id={errorMsgHolderId}>{errorMessage}</ErrorText>}
    </div>
  );
};

const DefaultDecoration = ({
  labelClasses = '',
  className = '',
  ariaHidden = false,
  title,
  description,
  errorMessage,
  errorMsgHolderId,
  inputId,
  children,
  toolTip,
  isFullWidth,
  color = 'primary',
  hideError = false,
  reducedMargin = false,
  noMargin = false,
  removeDescriptionMargin = false,
  componentType,
}: DecorationProps) => {
  let tabIndex = {};
  if (componentType === 'buttonGroup') {
    tabIndex = { tabIndex: -1 };
  }
  return (
    <div
      className={cx(className, styles.inputGroup, {
        [styles.fullWidth]: isFullWidth,
        [styles.reducedMargin]: reducedMargin,
        [styles.noMargin]: noMargin,
      })}>
      {(title || toolTip) && (
        <div className={styles.titleWrapper}>
          {title && (
            <label className={cx(labelClasses, styles.title)} htmlFor={inputId} aria-hidden={ariaHidden} {...tabIndex}>
              <span className={styles[color]}>{title}</span>
            </label>
          )}

          {toolTip && (
            <Tooltip
              className={styles.toolTip}
              text={toolTip.text}
              fillColor={toolTip.fillColor}
              appearsFrom={toolTip.appearsFrom}>
              <ParsedText htmlString={toolTip.tip} />
            </Tooltip>
          )}
        </div>
      )}
      {description && (
        <div
          className={cx(styles.description, {
            [styles.removeDescriptionMargin]: removeDescriptionMargin,
          })}>
          <ParsedText htmlString={description} />
        </div>
      )}
      {children}
      {!hideError && <ErrorText id={errorMsgHolderId}>{errorMessage}</ErrorText>}
    </div>
  );
};

export { RadioButtonsDecoration, CheckboxDecoration, DefaultDecoration };
